import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { AudioButton } from '../components/AudioButton';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Heart,
  Check,
  X,
  Loader2,
  Volume2,
  Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Practice() {
  const { lessonId } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [exercises, setExercises] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [inputAnswer, setInputAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [hearts, setHearts] = useState(5);
  const [xpEarned, setXpEarned] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchExercises();
    fetchHearts();
  }, [lessonId]);

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API}/exercises/${lessonId}`, { withCredentials: true });
      setExercises(response.data || []);
    } catch (error) {
      toast.error('Failed to load exercises');
    } finally {
      setLoading(false);
    }
  };

  const fetchHearts = async () => {
    try {
      const response = await axios.get(`${API}/hearts`, { withCredentials: true });
      setHearts(response.data.hearts);
    } catch (error) {
      toast.error('Failed to load hearts');
    }
  };

  const currentExercise = exercises[currentIndex];

  const getQuestion = (exercise) => {
    if (language === 'de') return exercise.question_de;
    if (language === 'ar') return exercise.question_ar;
    return exercise.question_en;
  };

  const getExplanation = (exercise) => {
    if (language === 'de') return exercise.explanation_de;
    if (language === 'ar') return exercise.explanation_ar;
    return exercise.explanation_en;
  };

  const handleSubmit = async () => {
    if (submitting || showResult) return;
    
    const answer = currentExercise.type === 'multiple_choice' || currentExercise.type === 'match_pairs' 
      ? selectedAnswer 
      : inputAnswer;
    
    if (!answer.trim()) {
      toast.error('Please provide an answer');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/exercises/answer`, {
        exercise_id: currentExercise.exercise_id,
        answer: answer
      }, { withCredentials: true });

      setIsCorrect(response.data.is_correct);
      setExplanation(getExplanation(currentExercise));
      setShowResult(true);

      if (response.data.is_correct) {
        setXpEarned(prev => prev + response.data.xp_earned);
      } else {
        // Lose heart
        await axios.post(`${API}/hearts/lose`, {}, { withCredentials: true });
        setHearts(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      toast.error('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (hearts === 0) {
      toast.error('No hearts left! Practice flashcards to restore.');
      navigate('/flashcards');
      return;
    }

    if (currentIndex < exercises.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer('');
      setInputAnswer('');
      setShowResult(false);
      setIsCorrect(false);
    } else {
      // Practice complete
      toast.success(`Practice complete! You earned ${xpEarned} XP!`);
      navigate('/lessons');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isComplete = currentIndex >= exercises.length;

  return (
    <div className="min-h-screen bg-background" data-testid="practice-page">
      {/* Header */}
      <header className="sticky top-0 bg-background/80 backdrop-blur-md border-b border-border z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/lessons')}
            >
              <X className="h-5 w-5" />
            </Button>
            
            <div className="flex items-center gap-4">
              {/* Hearts */}
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Heart 
                    key={`heart-${i}`} 
                    className={`h-5 w-5 ${i < hearts ? 'text-red-500 fill-red-500' : 'text-slate-300'}`}
                  />
                ))}
              </div>
              
              {/* XP */}
              <Badge className="bg-amber-500">
                <Zap className="h-4 w-4 mr-1" />
                {xpEarned} XP
              </Badge>
            </div>
          </div>

          {/* Progress */}
          <Progress 
            value={((currentIndex + (showResult ? 1 : 0)) / exercises.length) * 100} 
            className="h-2"
          />
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {exercises.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-muted-foreground mb-4">No exercises available for this lesson yet.</p>
              <Button onClick={() => navigate('/lessons')}>Back to Lessons</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Exercise Type Badge */}
            <div className="mb-6">
              <Badge variant="outline" className="capitalize">
                {currentExercise?.type?.replace('_', ' ')}
              </Badge>
            </div>

            {/* Question */}
            <div className="flex items-center gap-3 mb-8">
              <h2 className="font-heading text-2xl font-bold">
                {getQuestion(currentExercise)}
              </h2>
              {currentExercise?.question_de && (
                <AudioButton 
                  text={currentExercise.question_de} 
                  voice="nova"
                  size="default"
                />
              )}
            </div>

            {/* Answer Options */}
            {(currentExercise?.type === 'multiple_choice' || currentExercise?.type === 'match_pairs') && (
              <div className="space-y-3 mb-8">
                {currentExercise.options?.map((option, idx) => (
                  <div
                    key={`opt-${option?.slice(0,12)}-${idx}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => !showResult && setSelectedAnswer(option)}
                    onKeyDown={(e) => e.key === 'Enter' && !showResult && setSelectedAnswer(option)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                      showResult
                        ? option === currentExercise.correct_answer
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : option === selectedAnswer
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                            : 'border-border opacity-50 cursor-default'
                        : selectedAnswer === option
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`option-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{option}</span>
                        <AudioButton 
                          text={option} 
                          voice="nova"
                          size="sm"
                        />
                      </div>
                      {showResult && option === currentExercise.correct_answer && (
                        <Check className="h-5 w-5 text-green-500" />
                      )}
                      {showResult && option === selectedAnswer && option !== currentExercise.correct_answer && (
                        <X className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Text Input for fill_blank and translate */}
            {(currentExercise?.type === 'fill_blank' || currentExercise?.type === 'translate') && (
              <div className="mb-8">
                <div className="flex items-center gap-2">
                  <Input
                    value={inputAnswer}
                    onChange={(e) => setInputAnswer(e.target.value)}
                    placeholder="Type your answer or use microphone..."
                    disabled={showResult}
                    className={`text-lg py-6 flex-1 ${
                      showResult
                        ? isCorrect
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-red-500 bg-red-50 dark:bg-red-900/20'
                        : ''
                    }`}
                    data-testid="answer-input"
                    onKeyDown={(e) => e.key === 'Enter' && !showResult && handleSubmit()}
                  />
                  {!showResult && (
                    <MicrophoneButton
                      onTranscription={(text) => setInputAnswer(text)}
                      size="lg"
                      showLabel={true}
                      className="text-primary"
                    />
                  )}
                </div>
                {showResult && !isCorrect && (
                  <p className="mt-2 text-green-600 font-medium">
                    Correct answer: {currentExercise.correct_answer}
                  </p>
                )}
              </div>
            )}

            {/* Result Feedback */}
            {showResult && (
              <Card className={`mb-6 ${isCorrect ? 'border-green-500 bg-green-50 dark:bg-green-900/10' : 'border-red-500 bg-red-50 dark:bg-red-900/10'}`}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-3 mb-2">
                    {isCorrect ? (
                      <>
                        <Check className="h-6 w-6 text-green-500" />
                        <span className="font-bold text-green-600">Correct!</span>
                      </>
                    ) : (
                      <>
                        <X className="h-6 w-6 text-red-500" />
                        <span className="font-bold text-red-600">Incorrect</span>
                      </>
                    )}
                  </div>
                  <p className="text-muted-foreground">{explanation}</p>
                </CardContent>
              </Card>
            )}

            {/* Action Button */}
            {!showResult ? (
              <Button 
                onClick={handleSubmit}
                disabled={submitting || (!selectedAnswer && !inputAnswer.trim())}
                className="w-full py-6 text-lg bg-teal-500 hover:bg-teal-600"
                data-testid="check-btn"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Check'}
              </Button>
            ) : (
              <Button 
                onClick={handleContinue}
                className={`w-full py-6 text-lg ${isCorrect ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
                data-testid="continue-btn"
              >
                Continue
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
